import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { IconPlus, IconX } from "@tabler/icons-react"
import { useQueryClient } from "@tanstack/react-query"
import clsx from "clsx"
import { useCallback, useState } from "react"
import { api } from "../../../api/client"
import { useStore } from "../../../store/useStore"
import type { PrimitiveDef, Scope } from "../../../types"
import {
  SCOPE_ICON_COMPONENTS,
  TYPE_COLORS,
  TYPE_ICONS,
  getPrimitiveDisplayTitle,
  getScopeDisplayName,
} from "../../config/scopeIcons"
import { ExporterIntegrationTestModal } from "../customer-conformity/ExporterIntegrationTestModal"
import { TemplateSettingsPanel } from "../customer-conformity/TemplateSettingsPanel"
import type { AutoPilotDef } from "../registry"
import { WebhookEmailSettings } from "../supplier/WebhookEmailSettings"
import type { FieldKey } from "./qa-library"
import { getQA } from "./qa-library"
import { useFullScopes } from "./useFullScopes"
import { virtuallyOpenAllAPs } from "./virtuallyOpenAPs"

const ALL_SCOPE_IDS = [
  "supplier",
  "substance",
  "component",
  "product",
  "customer",
]
const AP_SESSION_STORAGE_KEY = "qms.configuration_ap.session_id"

// ── Top-level page ─────────────────────────────────────────────────────────────

export function APSettingsPage({ ap }: { ap: AutoPilotDef }) {
  const [systemPrompt, setSystemPrompt] = useState(ap.systemPrompt)
  const { translate } = useTranslation()
  const apName = translate(ap.name as TranslationKeys)
  const apDescription = translate(ap.description as TranslationKeys)

  const { data: fullScopes = [] } = useFullScopes()

  return (
    <div className="h-full overflow-y-auto bg-grey2 px-6 py-6">
      <div className="mx-auto max-w-xl space-y-6">
        <section>
          <FieldLabel
            apId={ap.id}
            field="name"
            label={translate("QMS/SETTINGS/NAME")}
          />
          <p className="text-sm text-white">{apName}</p>
        </section>

        <section>
          <FieldLabel
            apId={ap.id}
            field="description"
            label={translate("QMS/SETTINGS/DESCRIPTION")}
          />
          <p className="text-sm text-white">{apDescription}</p>
        </section>

        <section>
          <FieldLabel
            apId={ap.id}
            field="systemPrompt"
            label={translate("QMS/SETTINGS/WORKFLOW")}
          />
          <textarea
            value={systemPrompt}
            onChange={(e) => setSystemPrompt(e.target.value)}
            rows={5}
            className="w-full resize-none rounded-lg border border-grey4 bg-grey1 px-3 py-2 text-sm leading-relaxed text-white placeholder-grey5 outline-hidden transition-colors focus:border-yellow1"
          />
        </section>

        <section>
          <FieldLabel
            apId={ap.id}
            field="scopeAccess"
            label={translate("QMS/SETTINGS/SCOPE_ACCESS")}
            className="mb-3"
          />
          <ScopeAccessSection ap={ap} fullScopes={fullScopes} />
        </section>

        {ap.apType === "importer" && (
          <section className="border-t border-grey4 pt-6">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-grey5">
              {translate("QMS/SETTINGS/INTEGRATIONS")}
            </p>
            <WebhookEmailSettings
              apId={ap.id}
              emailAddressDisplay={`${apName}@ap.turnus.ai`}
            />
          </section>
        )}

        {ap.apType === "configurator" && (
          <section className="border-t border-grey4 pt-6">
            <ResetSection />
          </section>
        )}

        {ap.apType === "exporter" && (
          <section className="border-t border-grey4 pt-6">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-grey5">
              {translate("QMS/SETTINGS/TEMPLATE")}
            </p>
            <TemplateSettingsPanel apId={ap.id} />
          </section>
        )}

        {ap.apType === "exporter" && (
          <section className="border-t border-grey4 pt-6">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-grey5">
              {translate("QMS/SETTINGS/INTEGRATIONS")}
            </p>
            <WebhookEmailSettings
              apId={ap.id}
              emailAddressDisplay={`${apName}@ap.turnus.ai`}
              TestModal={ExporterIntegrationTestModal}
            />
          </section>
        )}
      </div>
    </div>
  )
}

// ── Shared help button ────────────────────────────────────────────────────────

function HelpButton({ apId, field }: { apId: string; field: FieldKey }) {
  const { currentLanguage } = useTranslation()
  const { setPendingChatQA } = useStore()
  const qa = getQA(apId, field, currentLanguage)

  return (
    <div className="group relative">
      <button
        onClick={() =>
          setPendingChatQA({ question: qa.question, answer: qa.answer })
        }
        className="flex h-4 w-4 items-center justify-center rounded-full border border-grey4 text-[10px] font-bold text-grey5 transition-colors hover:border-yellow1 hover:text-yellow1"
      >
        ?
      </button>
      <div className="pointer-events-none absolute left-1/2 top-full z-50 mt-1.5 w-56 -translate-x-1/2 rounded-lg border border-grey4 bg-grey1 px-3 py-2 text-[11px] leading-relaxed text-grey6 opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
        {qa.tooltip}
        <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 border-4 border-transparent border-b-grey4" />
      </div>
    </div>
  )
}

// ── Field label with help icon ────────────────────────────────────────────────

function FieldLabel({
  apId,
  field,
  label,
  className,
}: {
  apId: string
  field: FieldKey
  label: string
  className?: string
}) {
  return (
    <div className={clsx("mb-1.5 flex items-center gap-1.5", className)}>
      <span className="text-xs font-semibold uppercase tracking-wide text-grey5">
        {label}
      </span>
      <HelpButton apId={apId} field={field} />
    </div>
  )
}

// ── Read-only field ────────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function ReadOnlyField({
  label,
  value,
  href,
  apId,
  field,
}: {
  label: string
  value: string
  href?: string
  apId?: string
  field?: FieldKey
}) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-grey4/50 bg-grey1 px-3 py-2">
      <span className="w-10 shrink-0 text-[10px] font-semibold uppercase tracking-wide text-grey5">
        {label}
      </span>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          className="font-mono flex-1 truncate text-xs text-yellow1 hover:underline"
        >
          {value}
        </a>
      ) : (
        <span className="font-mono flex-1 truncate text-xs text-grey6">
          {value}
        </span>
      )}
      {apId && field && <HelpButton apId={apId} field={field} />}
    </div>
  )
}

// ── Scope access picker ────────────────────────────────────────────────────────

function ScopeAccessSection({
  ap,
  fullScopes,
}: {
  ap: AutoPilotDef
  fullScopes: Scope[]
}) {
  const initial =
    ap.scope.visibleScopeIds.length === 0
      ? [...ALL_SCOPE_IDS]
      : [...ap.scope.visibleScopeIds]

  const [enabledScopeIds, setEnabledScopeIds] = useState<string[]>(initial)
  const [disabledPrimIds, setDisabledPrimIds] = useState<
    Record<string, Set<string>>
  >({})
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const addScope = (id: string) => {
    setEnabledScopeIds((prev) => [...prev, id])
    setDisabledPrimIds((prev) => {
      const n = { ...prev }
      delete n[id]
      return n
    })
  }

  const removeScope = (id: string) => {
    setEnabledScopeIds((prev) => prev.filter((x) => x !== id))
    setExpanded((prev) => {
      const n = new Set(prev)
      n.delete(id)
      return n
    })
  }

  const disablePrimitive = (scopeId: string, primId: string) => {
    setDisabledPrimIds((prev) => ({
      ...prev,
      [scopeId]: new Set([...(prev[scopeId] ?? []), primId]),
    }))
  }

  const enablePrimitive = (scopeId: string, primId: string) => {
    setDisabledPrimIds((prev) => {
      const next = new Set(prev[scopeId] ?? [])
      next.delete(primId)
      return { ...prev, [scopeId]: next }
    })
  }

  const toggleExpanded = (id: string) => {
    setExpanded((prev) => {
      const n = new Set(prev)
      void (n.has(id) ? n.delete(id) : n.add(id))
      return n
    })
  }

  return (
    <div className="space-y-1.5">
      {ALL_SCOPE_IDS.map((id) => {
        const active = enabledScopeIds.includes(id)
        const scope = fullScopes.find((s) => s.id === id)
        const primitives = scope?.primitives ?? []
        const disabledSet = disabledPrimIds[id] ?? new Set<string>()

        return (
          <ScopeCard
            key={id}
            scopeId={id}
            scopeName={scope?.name ?? id}
            active={active}
            primitives={primitives}
            disabledPrimIds={disabledSet}
            isExpanded={expanded.has(id)}
            onToggleExpand={() => toggleExpanded(id)}
            onAddScope={() => addScope(id)}
            onRemoveScope={() => removeScope(id)}
            onEnablePrimitive={(pid) => enablePrimitive(id, pid)}
            onDisablePrimitive={(pid) => disablePrimitive(id, pid)}
          />
        )
      })}
    </div>
  )
}

// ── Unified scope card ─────────────────────────────────────────────────────────

function ScopeCard({
  scopeId,
  scopeName,
  active,
  primitives,
  disabledPrimIds,
  isExpanded,
  onToggleExpand,
  onAddScope,
  onRemoveScope,
  onEnablePrimitive,
  onDisablePrimitive,
}: {
  scopeId: string
  scopeName: string
  active: boolean
  primitives: PrimitiveDef[]
  disabledPrimIds: Set<string>
  isExpanded: boolean
  onToggleExpand: () => void
  onAddScope: () => void
  onRemoveScope: () => void
  onEnablePrimitive: (id: string) => void
  onDisablePrimitive: (id: string) => void
}) {
  const { translate, currentLanguage } = useTranslation()
  const Icon = SCOPE_ICON_COMPONENTS[scopeId]
  const name = getScopeDisplayName(
    { id: scopeId, name: scopeName },
    currentLanguage,
  )

  const activePrims = primitives.filter((p) => !disabledPrimIds.has(p.id))
  const inactivePrims = primitives.filter((p) => disabledPrimIds.has(p.id))

  const countLabel =
    primitives.length === 0
      ? null
      : disabledPrimIds.size === 0
        ? `${primitives.length} ${translate("QMS/SETTINGS/FIELDS")}`
        : `${activePrims.length} / ${primitives.length} ${translate("QMS/SETTINGS/FIELDS")}`

  return (
    <div
      className={clsx(
        "overflow-hidden rounded-lg border transition-colors",
        active ? "border-grey4 bg-grey1" : "border-grey4/30 bg-grey2",
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2.5">
        {/* Left: expand chevron + icon + name + count — dimmed when inactive */}
        <div
          className={clsx(
            "flex min-w-0 flex-1 items-center gap-2 transition-opacity",
            !active && "opacity-35",
          )}
        >
          <button
            onClick={active ? onToggleExpand : undefined}
            disabled={!active}
            className={clsx(
              "flex min-w-0 flex-1 items-center gap-2 text-left",
              active && "cursor-pointer",
            )}
          >
            <svg
              className={clsx(
                "h-3.5 w-3.5 shrink-0 text-grey5 transition-transform",
                active ? isExpanded && "rotate-90" : "invisible",
              )}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8.25 4.5l7.5 7.5-7.5 7.5"
              />
            </svg>
            {Icon && <Icon size={15} className="shrink-0 text-grey5" />}
            <span
              className={clsx(
                "text-sm font-medium",
                active ? "text-white" : "text-grey6",
              )}
            >
              {name}
            </span>
            {countLabel && (
              <span className="ml-1 text-[11px] text-grey5">{countLabel}</span>
            )}
          </button>
        </div>

        {/* Right: action button — always full opacity */}
        <button
          onClick={active ? onRemoveScope : onAddScope}
          title={
            active
              ? translate("QMS/AUTOPILOT/REMOVE_SCOPE")
              : translate("QMS/AUTOPILOT/ADD_SCOPE")
          }
          className="shrink-0 rounded p-1 text-grey5 transition-colors hover:bg-grey3 hover:text-white"
        >
          {active ? <IconX size={13} /> : <IconPlus size={13} />}
        </button>
      </div>

      {/* Primitive list — only when active + expanded */}
      {active && isExpanded && (
        <div className="border-t border-grey4">
          {primitives.length === 0 ? (
            <p className="px-4 py-3 text-xs italic text-grey5">
              {translate("QMS/SETTINGS/NO_FIELDS_CONFIGURED")}
            </p>
          ) : (
            <>
              {activePrims.map((p) => (
                <PrimitiveRow
                  key={p.id}
                  primitive={p}
                  enabled
                  onAction={() => onDisablePrimitive(p.id)}
                />
              ))}
              {inactivePrims.length > 0 && (
                <>
                  <div className="flex items-center gap-2 px-4 py-1.5">
                    <div className="h-px flex-1 bg-grey4" />
                    <span className="text-[10px] text-grey5">
                      {translate("QMS/SETTINGS/NO_ACCESS")}
                    </span>
                    <div className="h-px flex-1 bg-grey4" />
                  </div>
                  {inactivePrims.map((p) => (
                    <PrimitiveRow
                      key={p.id}
                      primitive={p}
                      enabled={false}
                      onAction={() => onEnablePrimitive(p.id)}
                    />
                  ))}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

// ── Primitive row ──────────────────────────────────────────────────────────────

function PrimitiveRow({
  primitive,
  enabled,
  onAction,
}: {
  primitive: PrimitiveDef
  enabled: boolean
  onAction: () => void
}) {
  const { translate, currentLanguage } = useTranslation()
  const typeStyle = TYPE_COLORS[primitive.type] ?? {
    bg: "bg-grey3 border-grey4",
    text: "text-grey5",
  }

  return (
    <div
      className={clsx(
        "flex items-center gap-2 border-b border-grey4/40 px-4 py-2 last:border-b-0",
        !enabled && "opacity-40",
      )}
    >
      <span
        className={`shrink-0 rounded border px-1 py-0.5 text-[10px] font-bold ${typeStyle.bg} ${typeStyle.text}`}
      >
        {TYPE_ICONS[primitive.type] ?? "?"}
      </span>
      {primitive.is_pk && (
        <span className="bg-yellow-100 text-yellow-700 flex h-4 w-5 shrink-0 items-center justify-center rounded text-[9px] font-bold">
          PK
        </span>
      )}
      <span className="flex-1 truncate text-sm text-white">
        {getPrimitiveDisplayTitle(primitive, currentLanguage)}
      </span>
      <button
        onClick={onAction}
        title={
          enabled
            ? translate("QMS/AUTOPILOT/REMOVE_FIELD")
            : translate("QMS/AUTOPILOT/ADD_FIELD")
        }
        className="shrink-0 rounded p-1 text-grey5 transition-colors hover:bg-grey3 hover:text-white"
      >
        {enabled ? <IconX size={12} /> : <IconPlus size={12} />}
      </button>
    </div>
  )
}

// ── Reset section (Configurator AP only) ──────────────────────────────────────

function ResetSection() {
  const { translate } = useTranslation()
  const queryClient = useQueryClient()
  const [resetting, setResetting] = useState(false)
  const [resettingDemo, setResettingDemo] = useState(false)

  const handleReset = useCallback(async () => {
    if (resetting) return
    if (!window.confirm(translate("QMS/SETTINGS/RESET_DB_CONFIRM"))) {
      return
    }
    setResetting(true)
    try {
      await api.resetDb()
      const leftovers = await api.sessionList().catch(() => [])
      if (leftovers.length > 0) {
        throw new Error(
          translate("QMS/SETTINGS/RESET_INCOMPLETE", {
            count: leftovers.length,
          }),
        )
      }
      try {
        localStorage.removeItem(AP_SESSION_STORAGE_KEY)
      } catch {
        // best-effort; non-fatal
      }
      queryClient.clear()
      window.location.reload()
    } catch (err) {
      setResetting(false)
      window.alert(
        err instanceof Error
          ? translate("QMS/SETTINGS/RESET_FAILED", { message: err.message })
          : translate("QMS/SETTINGS/RESET_FAILED_GENERIC"),
      )
    }
  }, [resetting, queryClient, translate])

  const handleResetDemo = useCallback(async () => {
    if (resettingDemo) return
    if (!window.confirm(translate("QMS/SETTINGS/RESET_DEMO_CONFIRM"))) {
      return
    }
    setResettingDemo(true)
    try {
      await api.resetDemo()
      virtuallyOpenAllAPs()
      queryClient.clear()
      window.location.reload()
    } catch (err) {
      setResettingDemo(false)
      window.alert(
        err instanceof Error
          ? translate("QMS/SETTINGS/RESET_DEMO_FAILED", {
              message: err.message,
            })
          : translate("QMS/SETTINGS/RESET_DEMO_FAILED_GENERIC"),
      )
    }
  }, [resettingDemo, queryClient, translate])

  return (
    <div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-grey5">
        {translate("QMS/SETTINGS/DATABASE")}
      </p>
      <div className="rounded-lg border border-grey4 bg-grey1 p-4">
        <p className="mb-0.5 text-sm font-medium text-white">
          {translate("QMS/SETTINGS/RESET_DATABASE")}
        </p>
        <p className="mb-3 text-xs text-grey5">
          {translate("QMS/SETTINGS/RESET_DESCRIPTION")}
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={handleReset}
            disabled={resetting || resettingDemo}
            className="rounded-lg border border-red1/40 px-4 py-2 text-sm text-red1 hover:bg-red1/10 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {resetting
              ? translate("QMS/SETTINGS/RESETTING")
              : translate("QMS/SETTINGS/RESET")}
          </button>
          <button
            onClick={handleResetDemo}
            disabled={resetting || resettingDemo}
            className="rounded-lg border border-yellow1/40 px-4 py-2 text-sm text-yellow1 hover:bg-yellow1/10 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {resettingDemo
              ? translate("QMS/SETTINGS/RESETTING_DEMO")
              : translate("QMS/SETTINGS/RESET_DEMO")}
          </button>
        </div>
      </div>
    </div>
  )
}
